import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { CircularProgress, Typography } from '@mui/material';
import { ChevronDownDashboardIcon as ChevronDownIcon } from '@truvhq/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import type { ChecklistStep, StepStatus } from 'entities/onboarding/types';
import { blockingStepId } from 'features/onboarding/lib/checklist';
import { useChecklistListOpen } from 'features/onboarding/lib/useChecklistListOpen';
import { useOnboardingChecklist } from 'features/onboarding/lib/useOnboardingChecklist';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { ChecklistStepRow, COLLAPSE_TRANSITION } from 'features/onboarding/ui/ChecklistStepRow';
import { ChecklistStepActions } from 'features/onboarding/ui/checklistStep/ChecklistStepActions';

// What the accordion is showing. `null` follows the current step as the checklist advances; a step
// id pins that step open; COLLAPSED is the user having closed the one that was open.
const COLLAPSED = Symbol('collapsed');
type Expansion = string | typeof COLLAPSED | null;

/**
 * The setup checklist as a region of the assistant panel.
 *
 * `children` receives the list and decides where it goes, because only the conversation knows where
 * its thread ends and its composer begins — the list sits behind the thread, and the thread slides
 * off it. The conversation stays mounted throughout, so its scroll position and any in-flight stream
 * survive opening and closing the list.
 */
export type ChecklistSlot = {
  /** The step list, or null. The conversation renders it behind its thread. */
  list: ReactNode;
  /** Whether the list is showing, so the conversation knows to slide off it. */
  isListOpen: boolean;
  /** Called when the user asks the assistant something, so the list gets out of the way. */
  onAsk: () => void;
};

export function ChecklistPane({ children }: { children: (slot: ChecklistSlot) => ReactNode }) {
  const checklist = useOnboardingChecklist();
  const [listPreference, rememberListPreference] = useChecklistListOpen();
  // Asking a question isn't a preference, so it is session state rather than something stored — but
  // it does outlive this mount, so hiding the panel and opening it again comes back to the
  // conversation rather than to the list the user had already pushed aside.
  const isDismissed = useOnboardingDialogStore((state) => state.isChecklistDismissed);
  const dismissChecklist = useOnboardingDialogStore((state) => state.dismissChecklist);
  const restoreChecklist = useOnboardingDialogStore((state) => state.restoreChecklist);
  const [expansion, setExpansion] = useState<Expansion>(null);
  // Whether there was still setup to do when the panel opened. Finishing the last step leaves the
  // checklist on screen — the user has earned seeing it complete — but the next open is just a chat.
  const [wasUnfinishedOnOpen, setWasUnfinishedOnOpen] = useState<boolean | null>(null);
  const prefersReducedMotion = useReducedMotion();
  // Someone part-way through would otherwise open on a list scrolled to the top, with the step they
  // are on below the fold. A ref callback fires exactly when that row mounts, which is the moment
  // there is something to scroll to; an effect had to guess at it from state that is already settled
  // by then, so it ran while the list was still behind the loading branch and scrolled nothing.
  const focusCurrentRow = useCallback((row: HTMLLIElement | null) => {
    row?.scrollIntoView({ block: 'center' });
  }, []);

  const hasCurrentStep = checklist.status === 'ready' && checklist.current !== null;
  const isChecklistSettled = checklist.status === 'ready';
  useEffect(() => {
    if (isChecklistSettled) {
      setWasUnfinishedOnOpen((known) => known ?? hasCurrentStep);
    }
  }, [isChecklistSettled, hasCurrentStep]);

  // Whatever the user last chose. Until they touch the toggle the catalog decides: `narrative` opens
  // collapsed to the header, `full` opens expanded. Read here rather than after the guards below so
  // the effect that follows can be a hook like any other — before the catalog loads there is nothing
  // to fall back on, and nothing to scroll to either.
  const displayMode = checklist.status === 'ready' ? checklist.checklist.display_mode : null;
  const listOpen = !isDismissed && (listPreference ?? displayMode === 'full');

  if (checklist.status === 'unavailable' || wasUnfinishedOnOpen === false) {
    return <>{children({ list: null, isListOpen: false, onAsk: () => undefined })}</>;
  }

  if (checklist.status === 'loading' || wasUnfinishedOnOpen === null) {
    return (
      <>
        <div className="flex flex-none items-center gap-2 border-grey30 border-b px-6 py-3 max-md:px-4">
          <CircularProgress size={16} />
          <Typography variant="body2" className="text-grey60">
            Loading your setup steps…
          </Typography>
        </div>
        {children({ list: null, isListOpen: false, onAsk: () => undefined })}
      </>
    );
  }

  const { sections, total_count } = checklist.checklist;
  const { steps, current } = checklist;
  const expandedStepId = expansion === COLLAPSED ? null : (expansion ?? current?.step_id ?? null);
  const isHeaderDetailOpen = !listOpen && Boolean(current);
  const slide = prefersReducedMotion ? { duration: 0 } : COLLAPSE_TRANSITION;

  // An explicit toggle records the preference and brings the session state with it, so the choice
  // isn't overruled by a dismissal it supersedes.
  const toggleList = () => {
    const next = !listOpen;
    if (next) {
      restoreChecklist();
    } else {
      dismissChecklist();
    }
    rememberListPreference(next);
  };

  const settle = (step: ChecklistStep, status: StepStatus) =>
    checklist.settle(step, status).then((changed) => {
      if (!changed) {
        return;
      }
      // Ticking a step off moves the user on, so the expansion follows to whatever is current now.
      // Undo is the opposite: they're going back to this step, so it stays open under them.
      setExpansion(status === 'pending' ? step.step_id : null);
    });

  return (
    <>
      {/* Sits above the list so the list scrolls under it rather than across it. */}
      <div className="relative z-[6] flex-none border-grey30 border-b bg-white pb-3">
        <button
          type="button"
          onClick={toggleList}
          aria-expanded={listOpen}
          className="flex w-full cursor-pointer items-center gap-2.5 bg-transparent px-6 pt-3 pb-1 text-left max-md:px-4"
        >
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            {current && (
              <Typography variant="body2" className="font-medium text-grey60 text-xs">
                {`Step ${steps.indexOf(current) + 1} of ${total_count}`}
              </Typography>
            )}
            <Typography className="truncate font-medium text-grey90 text-sm">
              {current?.title ?? 'Setup complete'}
            </Typography>
          </span>
          <motion.span
            className="flex shrink-0"
            animate={{ rotate: listOpen ? 180 : 0 }}
            transition={slide}
            aria-hidden
          >
            <ChevronDownIcon className="h-5 w-5 text-grey60" />
          </motion.span>
        </button>
        {/* Collapsed, the header is the current step: its detail comes with it so the step stays
            actionable without reopening the list. */}
        <motion.div
          className="overflow-hidden"
          initial={false}
          animate={{ height: isHeaderDetailOpen ? 'auto' : 0, opacity: isHeaderDetailOpen ? 1 : 0 }}
          transition={slide}
          aria-hidden={!isHeaderDetailOpen}
        >
          <div className="flex flex-col px-6 max-md:px-4">
            {current?.description && (
              <Typography variant="body2" className="text-grey90">
                {current.description}
              </Typography>
            )}
            {current && (
              <ChecklistStepActions
                step={current}
                blockerTitle={checklist.blockerTitle(current)}
                onOpenBlocker={() => setExpansion(blockingStepId(current))}
                busy={checklist.pendingAction(current.step_id)}
                onSettle={(status) => settle(current, status)}
              />
            )}
          </div>
        </motion.div>
      </div>

      {children({
        onAsk: dismissChecklist,
        isListOpen: listOpen,
        // Held through the conversation's slide back over it, so the list doesn't vanish from under
        // a surface that is still moving.
        list: (
          <AnimatePresence>
            {listOpen && (
              <motion.section
                aria-label="Setup steps"
                className="absolute inset-0 overflow-y-auto bg-grey20 px-6 py-4"
                initial={false}
                exit={{ opacity: 1 }}
                transition={slide}
              >
                <div className="flex flex-col gap-4">
                  {sections.map((section) => (
                    <div key={section.section_id} className="flex flex-col gap-2">
                      {/* A single section reads as "the checklist"; more than one (dashboard plus an
                      integration) needs its headings to stay meaningful. */}
                      {sections.length > 1 && (
                        <Typography variant="body2" className="font-semibold text-grey70">
                          {section.section_label}
                        </Typography>
                      )}
                      <ul className="flex list-none flex-col gap-2 p-0">
                        {section.steps.map((step) => (
                          <ChecklistStepRow
                            key={step.step_id}
                            ref={step.step_id === current?.step_id ? focusCurrentRow : undefined}
                            step={step}
                            blockerTitle={checklist.blockerTitle(step)}
                            isExpanded={expandedStepId === step.step_id}
                            onExpand={() => setExpansion(expandedStepId === step.step_id ? COLLAPSED : step.step_id)}
                            onOpenBlocker={() => setExpansion(blockingStepId(step))}
                            onSettle={(status) => settle(step, status)}
                            busy={checklist.pendingAction(step.step_id)}
                          />
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        ),
      })}
    </>
  );
}
